import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { containerSx, contentGutter, fluidContainerSx } from './constants';
import { isNavItemActive } from './nav-match';
import type { SxProps } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type { AppShellProps, NavItem } from './types';

type TopNavProps = {
  navItems: NavItem[];
  currentPath: string;
  linkComponent: AppShellProps['linkComponent'];
  emitDocAttributes: boolean;
  navActions?: ReactNode;
  fluid?: boolean;
};

export function TopNav({
  navItems,
  currentPath,
  linkComponent,
  emitDocAttributes,
  navActions,
  fluid = false,
}: TopNavProps) {
  const activeValue = navItems.find((item) => isNavItemActive(item, currentPath))?.to ?? false;
  let navContainerSx: SxProps = containerSx;
  if (fluid) {
    navContainerSx = fluidContainerSx;
  }

  return (
    <Box component="nav" aria-label="Primary" sx={navContainerSx}>
      {/* The same gutters as the bar above it, so the tabs start on the rail's edge and the
          divider ends where the page content ends. */}
      <Box
        sx={{
          mr: contentGutter,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Tabs
          value={activeValue}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flexGrow: 1,
            minWidth: 0,
            '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
            '& .MuiTab-root:first-of-type': { pl: 0 },
          }}
        >
          {navItems.map((item) => {
            const linkProps = item.spa
              ? { component: linkComponent, to: item.to }
              : { component: 'a' as const, href: item.to };
            const docProps = emitDocAttributes
              ? { 'data-doc-action': 'navigate', 'data-doc-context': item.match }
              : {};
            return (
              <Tab
                key={item.to}
                value={item.to}
                label={item.label}
                onClick={item.onClick}
                {...linkProps}
                {...docProps}
              />
            );
          })}
        </Tabs>
        {navActions ? <Box sx={{ flexShrink: 0 }}>{navActions}</Box> : null}
      </Box>
    </Box>
  );
}
